import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Subscription, Observable } from 'rxjs';
import { HotToastService } from '@ngneat/hot-toast';
import { HttpErrorResponse } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { Router } from '@angular/router';

import { CheckOutReviewInfoDto, PaymentMethodDto, PaymentProcessingRequest, ShoppingCartDto, TestSessionForRegistrationDto, UserAddressReviewDto } from 'src/app/core/dtos/dto';
import { CheckoutService, CheckoutWorkflowEnum } from 'src/app/core/services/checkout.service';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { LoadingService } from 'src/app/core/services/loading.service';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { ShoppingCartService } from 'src/app/core/services/shopping-cart.service';
import { CheckoutStepEnum, StepProgressService } from 'src/app/core/services/step-progress.service';
import { Step } from 'src/app/core/dtos/step';
import { CheckoutStepsService } from 'src/app/core/services/checkout-steps.service';
import { BrandEnum, ProductTypeEnum } from 'src/app/core/dtos/enum';
import { selectIsBusinessAccount } from 'src/app/state/my-account.selectors';
import { take } from 'rxjs/operators';
import { setRegTestSession, setTestPurposeOption } from 'src/app/state/reg-data.actions';
import { selectRegTestSession } from 'src/app/state/reg-data.selectors';
import { TestPurposeOption } from 'src/app/core/dtos/test-purpose-option';
import { RegistrationService } from 'src/app/core/services/registration.service';
import { loadShoppingCart } from 'src/app/state/shopping-cart.actions';


@Component({
  selector: 'app-review',
  templateUrl: './review.component.html',
  styleUrls: ['./review.component.scss']
})
export class ReviewComponent implements OnInit, OnDestroy {

  testSessionInformation$: Observable<TestSessionForRegistrationDto>;

  shoppingCart: ShoppingCartDto;
  checkoutReviewInfo: CheckOutReviewInfoDto;
  checkoutReviewInfoSubscription: Subscription;
  checkoutSubscription: Subscription;
  discountSusbscription: Subscription;
  shoppingCartSubscription: Subscription;
  testSessionInformationSubscription: Subscription;
  onlinePaymentSubscription: Subscription;
  otherPaymentSubscription: Subscription;
  regPoliciesUrl: string;
  brandId: BrandEnum;
  total: number;
  billingAddress: UserAddressReviewDto;
  shippingAddress: UserAddressReviewDto;
  paymentMethods: PaymentMethodDto[];
  steps: Step[];
  selectedPaymentMethodId: number;
  selectedPaymentMethodName: string;
  paymentProcessingRequest: PaymentProcessingRequest;
  browserRequirementsUrl: string;
  isBusinessAccount: boolean;
  currentStep = 3;
  paymentMethodInvoiceId = 14;
  paymentInfo: string;
  hasOSRReg: boolean;
  arrivalTimeCheck: boolean;

  constructor(
    private checkoutService: CheckoutService,
    private shoppingCartService: ShoppingCartService,
    private registrationService: RegistrationService,
    private toastService: HotToastService,
    private configHelper: ConfigHelper,
    private loadingService: LoadingService,
    private stepProgressService: StepProgressService,
    private router: Router,
    private checkoutStepsService: CheckoutStepsService,
    private store: Store
  ) {
    this.steps = this.stepProgressService.getCheckoutSteps(CheckoutStepEnum.ReviewAndPayment);
  }

  ngOnInit(): void {
    this.brandId = this.configHelper.getBrandId();
    this.regPoliciesUrl = this.configHelper.getRegPoliciesUrl();
    this.browserRequirementsUrl = (this.brandId === this.BrandEnum.CELPIP) ? "https://www.celpip.ca/faqs/" : "https://www.cael.ca/faqs/";
    this.getCheckoutReviewInfo();
  }

  public get BrandEnum() {
    return BrandEnum;
  }

  getCheckoutReviewInfo() {
    this.loadingService.show();
    this.checkoutReviewInfoSubscription = this.checkoutService.updateFinalInfo().
      subscribe(
        result => {
          this.checkoutReviewInfo = result.content;
          this.checkoutStepsService.verifyData(this.currentStep, result.content.allowedStep);
          this.billingAddress = this.checkoutReviewInfo.billingAddress;
          this.shippingAddress = this.checkoutReviewInfo.shippingAddress;
          this.paymentInfo = this.checkoutReviewInfo.paymentInfo;
          if (this.checkoutReviewInfo.additionalPaymentMethods) {
            this.paymentMethods = this.checkoutReviewInfo.additionalPaymentMethods;
            if (this.paymentMethods.length === 1) {
              this.selectedPaymentMethodId = this.paymentMethods[0].id;
              this.selectedPaymentMethodName = this.paymentMethods[0].name;
            }
          }
          this.getShoppingCart();
          this.loadingService.hide();
        },
        () => {
          this.loadingService.hide();
        });
  }

  getShoppingCart() {
    this.shoppingCartSubscription = forkJoin([this.shoppingCartService.getShoppingCart(), this.store.select(selectIsBusinessAccount).pipe(take(1))])
      .subscribe(
        result => {
          //check for Reg, then redirect as needed
          if (!result[1] && result[0].content.shoppingCartItems.some(e => e.productTypeId === ProductTypeEnum.TestRegistration)) {
            this.checkoutService.setLastCheckoutFlow(CheckoutWorkflowEnum.RegCheckOut);
            this.testSessionInformation$ = this.store.pipe(select(selectRegTestSession));
            this.testSessionInformationSubscription = this.testSessionInformation$
              .subscribe(sessionInfo => {
                if (sessionInfo == null) {
                  //fill in store again with values from backend if reg got cleared from store
                  const reg = result[0].content.shoppingCartItems.find(e => e.productTypeId === ProductTypeEnum.TestRegistration);
                  this.checkoutSubscription = forkJoin([this.checkoutService.getShoppingCartReg(reg.userAccountId), this.checkoutService.getShoppingCartRegTestPurpose(reg.userAccountId)])
                    .subscribe(checkoutServiceResult => {
                      this.registrationService.getTestSessionInfo(checkoutServiceResult[0].content.siteCode, checkoutServiceResult[0].content.testTypeId)
                        .subscribe(testSession => {
                          const testSessionReg = testSession.content;
                          this.store.dispatch(setRegTestSession({ testSession: testSessionReg }));
                          this.loadingService.hide();
                          this.checkoutService.redirectCheckoutBasedOnFlow(CheckoutWorkflowEnum.NoRegCheckout);
                        });
                      const testPurpose: TestPurposeOption = {
                        id: checkoutServiceResult[1].content.id.toString(),
                        parentId: null,
                        testPurposeId: checkoutServiceResult[1].content.id,
                        institutionId: null,
                        name: checkoutServiceResult[1].content.description,
                        children: null
                      };
                      this.store.dispatch(setTestPurposeOption({ testPurposeOption: testPurpose }));
                    });
                } else {
                  this.loadingService.hide();
                  this.checkoutService.redirectCheckoutBasedOnFlow(CheckoutWorkflowEnum.NoRegCheckout);
                }
              });
          } else {
            this.checkoutService.setLastCheckoutFlow(CheckoutWorkflowEnum.NoRegCheckout);
            this.loadingService.hide();
            this.checkoutService.redirectCheckoutBasedOnFlow(CheckoutWorkflowEnum.NoRegCheckout);
          }
          this.store.dispatch(loadShoppingCart({ shoppingCart: result[0].content }));
          this.shoppingCart = result[0].content;
          this.total = this.shoppingCart.total;
          this.hasOSRReg = !!this.shoppingCart.shoppingCartItems.find(i => i.productTypeId == ProductTypeEnum.RetakeTestRegistration);
        },
        () => this.toastService.warning("Unable to load page")
      );
  }

  applyDiscountCode(discountCode: string) {
    this.loadingService.show();
    this.discountSusbscription = this.checkoutService.applyDiscountCode(discountCode)
      .subscribe(
        () => {
          this.ngOnInit();
          this.loadingService.hide();
        },
        (error: HttpErrorResponse) => {
          this.loadingService.hide();
          if (error.status === 400) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
          else {
            this.toastService.warning("Failed to place the order because of unknown problem. Please contact Paragon.");
          }
        }
      );
  }

  selectPaymentMethod(paymentMethod: PaymentMethodDto) {
    this.selectedPaymentMethodId = paymentMethod.id;
    this.selectedPaymentMethodName = paymentMethod.name;
  }

  placeOrder() {
    if ((!this.selectedPaymentMethodId || this.selectedPaymentMethodId === 1) && this.total > 0) {
      this.onlinePaymentCheckout();
    } else {
      this.otherPaymentMethodCheckout();
    }
  }

  otherPaymentMethodCheckout() {
    this.loadingService.show();
    this.otherPaymentSubscription = this.checkoutService.placeOrder(this.selectedPaymentMethodId)
      .subscribe(
        (result) => {
          this.loadingService.hide();
          this.checkoutService.setLastCheckoutOrderNumber(result.content.orderNumber, CheckoutWorkflowEnum.NoRegCheckout);
          this.router.navigate(['checkout/done']);
        },
        (error: HttpErrorResponse) => {
          this.loadingService.hide();
          if (error.status !== 401) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            if (errorMessage?.reasons) {
              errorMessage.reasons.forEach(reasons => {
                this.toastService.warning(reasons.errorMessage);
              });
            }
            else {
              this.toastService.warning("Failed to place the order because of unknown problem. Please contact Paragon.");
            }
          }
        }
      );
  }

  onlinePaymentCheckout() {
    this.loadingService.show();
    this.onlinePaymentSubscription = this.checkoutService.placeOrder(null)
      .subscribe(
        (result) => {
          this.loadingService.hide();
          this.checkoutService.setLastCheckoutOrderNumber(result.content.orderNumber, CheckoutWorkflowEnum.NoRegCheckout);
          this.paymentProcessingRequest = result.content;
          if (this.paymentProcessingRequest.paymentGatewayLink === null || this.paymentProcessingRequest.paymentGatewayLink.paymentGatewayId === 1) {
            this.paymentExactSubmit(this.paymentProcessingRequest);
          }
          else if (this.paymentProcessingRequest.paymentGatewayLink.paymentGatewayId === 2) {
            this.paymentPaypalSubmit(this.paymentProcessingRequest);
          }
          else if (this.paymentProcessingRequest.paymentGatewayLink.paymentGatewayId === 3) {
            this.paymentCCAvenueSubmit(this.paymentProcessingRequest);
          }
          else if (this.paymentProcessingRequest.paymentGatewayLink.paymentGatewayId === 4) {
            this.paymentRiverPaySubmit(this.paymentProcessingRequest);
          }
          else if (this.paymentProcessingRequest.paymentGatewayLink.paymentGatewayId === 5) {
            this.paymentAlphaPaySubmit(this.paymentProcessingRequest);
          }
          else if (this.paymentProcessingRequest.paymentGatewayLink.paymentGatewayId === 6) {
            this.paymentPaymentechSubmit(this.paymentProcessingRequest);
          }
          else if (this.paymentProcessingRequest.paymentGatewayLink.paymentGatewayId === 7) {
            this.paymentStripePOSubmit(this.paymentProcessingRequest);
          }
          else {
            this.toastService.warning("Failed to place the order because of unknown problem. Please contact Paragon.");
          }
        },
        (error: HttpErrorResponse) => {
          this.loadingService.hide();
          if (error.status === 400) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
          else {
            this.toastService.warning("Failed to place the order because of unknown problem. Please contact Paragon.");
          }
        }
      );
  }

  paymentExactSubmit(content: PaymentProcessingRequest) {
    (document.getElementById('exactForm') as HTMLFormElement).setAttribute("action", content.exactUrl);
    (document.getElementById('x_login') as HTMLInputElement).value = content.x_login;
    (document.getElementById('x_fp_sequence') as HTMLInputElement).value = content.x_fp_sequence;
    (document.getElementById('x_fp_timestamp') as HTMLInputElement).value = content.x_fp_timestamp;
    (document.getElementById('x_amount') as HTMLInputElement).value = content.x_amount;
    (document.getElementById('x_fp_hash') as HTMLInputElement).value = content.x_fp_hash;
    (document.getElementById('x_show_form') as HTMLInputElement).value = content.x_show_form;
    (document.getElementById('x_currency_code') as HTMLInputElement).value = content.x_currency_code;
    (document.getElementById('x_first_name') as HTMLInputElement).value = content.x_first_name;
    (document.getElementById('x_last_name') as HTMLInputElement).value = content.x_last_name;
    (document.getElementById('x_email') as HTMLInputElement).value = content.x_email;
    (document.getElementById('x_invoice_num') as HTMLInputElement).value = content.x_invoice_num;
    (document.getElementById('x_reference_3') as HTMLInputElement).value = content.x_reference_3;
    (document.getElementById('userAccountId') as HTMLInputElement).value = content.userAccountId.toString();
    (document.getElementById('orderNumber') as HTMLInputElement).value = content.orderNumber;
    (document.getElementById('exactForm') as HTMLFormElement).submit();
  }

  paymentPaypalSubmit(content: PaymentProcessingRequest) {
    const paypalUrl = content.paymentGatewayLink.keyValues['approval_url'];
    window.location.href = paypalUrl;
  }

  paymentStripePOSubmit(content: PaymentProcessingRequest) {
    const paymentOrchestratorUrl = content.paymentGatewayLink.keyValues['paymentOrchestratorUrl'];
    window.location.href = paymentOrchestratorUrl;
  }

  paymentCCAvenueSubmit(content: PaymentProcessingRequest) {
    (document.getElementById('ccavenueForm') as HTMLFormElement).setAttribute('action', content.paymentGatewayLink.keyValues['payment_url']);
    (document.getElementById('encRequest') as HTMLInputElement).value = content.paymentGatewayLink.keyValues['encRequest'];
    (document.getElementById('access_code') as HTMLInputElement).value = content.paymentGatewayLink?.keyValues['access_code'];
    (document.getElementById('ccavenueForm') as HTMLFormElement).submit();
  }

  paymentRiverPaySubmit(content: PaymentProcessingRequest) {
    const riverPayPayFormUrl = content.paymentGatewayLink.keyValues['pay_form_url'];
    window.location.href = riverPayPayFormUrl;
  }

  paymentAlphaPaySubmit(content: PaymentProcessingRequest) {
    const alphaPayUrl = content.paymentGatewayLink.keyValues['alpha_pay_url'];
    window.location.href = alphaPayUrl;
  }

  paymentPaymentechSubmit(content: PaymentProcessingRequest) {
    const paymentechHPPUrl = content.paymentGatewayLink.keyValues['paymentech_hpp_url'];
    const paymentech_uid = content.paymentGatewayLink.keyValues['paymentech_uid'];
    (document.getElementById('uid') as HTMLInputElement).value = paymentech_uid;
    (document.getElementById('paymentechHPPForm') as HTMLFormElement).action = paymentechHPPUrl;
    (document.getElementById('paymentechHPPForm') as HTMLFormElement).submit();
  }

  ngOnDestroy(): void {
    this.checkoutReviewInfoSubscription?.unsubscribe();
    this.shoppingCartSubscription?.unsubscribe();
    this.discountSusbscription?.unsubscribe();
    this.onlinePaymentSubscription?.unsubscribe();
    this.otherPaymentSubscription?.unsubscribe();
    this.testSessionInformationSubscription?.unsubscribe();
    this.checkoutSubscription?.unsubscribe();
  }

}
